<template>
  <div class="space-y-6">
    <div class="flex flex-col gap-6 lg:flex-row">
      <TeacherGroupsSidebar
        :active-group-id="activeGroupId"
        :students-count="groupList.length"
        :group-list="groupList"
        :open-group-menu-id="openGroupMenuId"
        @select-group="setActiveGroup"
        @toggle-menu="toggleGroupMenu"
        @edit-group="openEditGroupModal"
        @clone-group="cloneGroup"
        @delete-group="deleteGroup"
        @create-group="openCreateGroupModal"
      />

      <section ref="studentsTableSection" class="flex-1 rounded-xl bg-white shadow p-6">
        <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-neutral-800">Mis Estudiantes</h1>
            <p class="text-sm text-neutral-500">
              Gestiona tus estudiantes y sus grupos.
            </p>
          </div>
          <button
            class="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
            @click="openInviteModalFromHeader"
          >
            Enviar Solicitud
          </button>
        </div>

        <div class="mt-5 flex flex-wrap items-center gap-3 md:justify-between">
          <div class="flex gap-2">
            <button
              v-for="tab in statusTabs"
              :key="tab.value"
              class="rounded-md px-4 py-2 text-sm font-medium"
              :class="activeStatus === tab.value ? 'bg-blue-600 text-white' : 'bg-neutral-100 text-neutral-600'"
              @click="activeStatus = tab.value; resetSelection();"
            >
              {{ tab.label }}
            </button>
          </div>

          <div class="flex items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-600">
            <svg class="h-4 w-4 text-neutral-500" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
              <path d="M20 20L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            <input
              v-model.trim="search"
              type="text"
              placeholder="Buscar estudiante..."
              class="w-full bg-transparent text-sm text-neutral-700 focus:outline-none"
            />
          </div>

          <select
            v-model="bulkAction"
            class="rounded-md border border-neutral-200 px-3 py-2 text-sm"
            :disabled="selectedStudents.length === 0"
            @change="handleBulkAction"
          >
            <option disabled value="">Acciones</option>
            <option v-for="option in bulkActionOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>

          <button
            class="inline-flex items-center justify-center rounded-md border border-neutral-200 p-2 text-neutral-600 hover:bg-neutral-50"
            @click="loadStudents"
            title="Actualizar"
            aria-label="Actualizar"
          >
            <IconifyIcon :icon="icons.bxRefresh" height="18" />
          </button>
        </div>

        <div class="mt-4 rounded-xl border border-neutral-200 overflow-x-auto md:overflow-visible">
          <table class="w-full text-left text-sm">
            <thead class="bg-neutral-50 text-neutral-600">
              <tr>
                <th class="px-4 py-3">
                  <input
                    type="checkbox"
                    class="h-4 w-4"
                    :checked="isAllSelected"
                    @change="toggleSelectAll"
                  />
                </th>
                <th v-if="activeStatus === 'accepted'" class="px-4 py-3"></th>
                <th class="px-4 py-3">Estudiante</th>
                <th class="px-4 py-3">Email</th>
                <th v-if="activeStatus === 'accepted'" class="px-4 py-3">
                  Fecha de aceptación
                </th>
                <th class="px-4 py-3">Grupos</th>
                <th class="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="student in paginatedStudents"
                :key="student.id"
                class="border-t border-neutral-100 hover:bg-neutral-50"
              >
                <td class="px-4 py-3">
                  <input
                    type="checkbox"
                    class="h-4 w-4"
                    :value="student.id"
                    v-model="selectedStudents"
                  />
                </td>
                <td v-if="activeStatus === 'accepted'" class="px-4 py-3">
                  <MobAvatar :text="student.name" />
                </td>
                <td class="px-4 py-3 font-medium text-neutral-700">
                  {{ student.name }}
                </td>
                <td class="px-4 py-3 text-neutral-500">{{ student.email }}</td>
                <td
                  v-if="activeStatus === 'accepted'"
                  class="px-4 py-3 text-neutral-500"
                >
                  {{ formatDate(student.acceptedAt) }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="group in student.groups"
                      :key="group"
                      class="rounded-md bg-blue-50 px-2 py-1 text-xs text-blue-700"
                      @click.stop="openEditGroupModalByName(group)"
                    >
                      {{ group }}
                    </button>
                    <span v-if="student.groups.length === 0" class="text-xs text-neutral-400">
                      No asignado
                    </span>
                  </div>
                </td>
                <td class="px-4 py-3 text-right">
                  <div class="relative inline-block text-left">
                    <button
                      class="rounded-md px-2 py-1 text-neutral-500 hover:bg-neutral-100"
                      @click.stop="toggleRowMenu(student)"
                      aria-label="Acciones"
                    >
                      &#8942;
                    </button>
                    <div
                      v-if="openRowMenuId === student.id"
                      class="hidden md:block absolute right-0 mt-2 w-56 rounded-md border border-neutral-200 bg-white shadow-lg z-10"
                    >
                      <button
                        v-if="student.status === 'accepted'"
                        class="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                        @click="goToStudentReport(student)"
                      >
                        Ver perfil / Reporte
                      </button>
                      <button
                        v-for="option in rowActionOptions(student)"
                        :key="option"
                        class="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                        @click="handleRowAction(student, option)"
                      >
                        {{ option }}
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-xs text-neutral-500">
            <span>
              {{ paginationStart }} a {{ paginationEnd }} de {{ filteredStudents.length }} estudiantes
            </span>
            <div class="flex items-center gap-2">
              <button
                class="rounded-md border border-neutral-200 px-2 py-1 disabled:opacity-60"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
              >
                Anterior
              </button>
              <button
                v-for="page in totalPages"
                :key="page"
                class="rounded-md border border-neutral-200 px-2 py-1"
                :class="page === currentPage ? 'bg-blue-600 text-white border-blue-600' : ''"
                @click="goToPage(page)"
              >
                {{ page }}
              </button>
              <button
                class="rounded-md border border-neutral-200 px-2 py-1 disabled:opacity-60"
                :disabled="currentPage === totalPages || totalPages === 0"
                @click="goToPage(currentPage + 1)"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Menú contextual móvil para acciones de estudiante -->
    <div
      v-if="currentRowMenuStudent && openRowMenuId === currentRowMenuStudent.id"
      class="fixed inset-0 z-[1200] flex items-end bg-black/40 px-4 pb-6 pt-12 md:hidden"
      @click.self="closeRowMenu"
    >
      <div class="w-full rounded-2xl bg-white shadow-xl">
        <div class="flex items-center justify-between px-4 pt-4 pb-2 border-b border-neutral-100">
          <div class="flex items-center gap-3">
            <MobAvatar :text="currentRowMenuStudent.name" class="h-9 w-9 flex-shrink-0" />
            <div class="flex flex-col">
              <span class="text-sm font-medium text-neutral-800">
                {{ currentRowMenuStudent.name }}
              </span>
              <span class="text-xs text-neutral-500">
                {{ currentRowMenuStudent.email }}
              </span>
            </div>
          </div>
          <button
            type="button"
            class="text-neutral-400 hover:text-neutral-600 text-xl leading-none px-2"
            @click="closeRowMenu"
          >
            ✕
          </button>
        </div>

        <div class="py-1">
          <button
            v-if="currentRowMenuStudent.status === 'accepted'"
            class="block w-full text-left px-4 py-3 text-sm text-neutral-800 hover:bg-neutral-50"
            @click="goToStudentReport(currentRowMenuStudent)"
          >
            Ver perfil / Reporte
          </button>
          <button
            v-for="option in rowActionOptions(currentRowMenuStudent)"
            :key="option"
            class="block w-full text-left px-4 py-3 text-sm text-neutral-800 hover:bg-neutral-50"
            @click="handleRowAction(currentRowMenuStudent, option)"
          >
            {{ option }}
          </button>
        </div>
      </div>
    </div>

    <TeacherCreateGroupModal
      :show="showCreateGroupModal"
      :form="form"
      :is-creating="isCreating"
      :show-validation="showValidation"
      :can-create="canCreate"
      @close="closeCreateGroupModal"
      @create="createGroup"
      @update-field="updateCreateGroupField"
    />

    <TeacherEditGroupModal
      :show="showEditGroupModal"
      :form="editGroupForm"
      :students="editGroupStudents"
      @close="closeEditGroupModal"
      @save="saveEditGroup"
      @remove-student="removeStudentFromEditGroup"
      @remove-selected="removeSelectedFromEditGroup"
      @add-students="openAddStudentsModal"
      @update-field="updateEditGroupField"
    />

    <div
      v-if="showAddStudentsModal"
      class="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4"
      @click.self="closeAddStudentsModal"
    >
      <div class="w-full max-w-lg rounded-xl bg-white p-6 shadow-lg">
        <div class="flex items-center justify-between mb-2">
          <h2 class="text-lg font-semibold text-neutral-800">Añadir estudiantes</h2>
          <button class="text-neutral-400 hover:text-neutral-600" @click="closeAddStudentsModal">
            ✕
          </button>
        </div>
        <p class="text-sm text-neutral-500 mb-4">
          Selecciona los estudiantes que aceptaron la invitación.
        </p>

        <div class="flex items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-600">
          <svg class="h-4 w-4 text-neutral-500" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
            <path d="M20 20L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          <input
            v-model.trim="addStudentsSearch"
            type="text"
            placeholder="Buscar estudiante..."
            class="w-full bg-transparent text-sm text-neutral-700 focus:outline-none"
          />
        </div>

        <div class="mt-3 rounded-lg border border-neutral-200">
          <div class="flex items-center justify-between px-4 py-2 border-b border-neutral-100 text-sm text-neutral-600">
            <label class="flex items-center gap-3">
              <input
                type="checkbox"
                class="form-checkbox h-4 w-4"
                :checked="addStudentsAllSelected"
                @change="toggleAddStudentsSelectAll"
              />
              <span>Estudiante</span>
            </label>
            <span>{{ addStudentsSelectedIds.length }} seleccionados</span>
          </div>

          <div class="max-h-56 overflow-y-auto">
            <label
              v-for="student in addStudentsFiltered"
              :key="student.id"
              class="flex items-center justify-between px-4 py-2 border-b border-neutral-100 last:border-b-0 text-sm"
            >
              <div class="flex items-center gap-3">
                <input
                  type="checkbox"
                  class="form-checkbox h-4 w-4"
                  :value="student.id"
                  v-model="addStudentsSelectedIds"
                />
                <div class="flex items-center gap-3">
                  <MobAvatar
                    :text="student.name"
                    class="h-9 w-9 flex-shrink-0"
                  />
                  <div class="flex flex-col">
                    <span class="text-neutral-700 font-medium">{{ student.name }}</span>
                    <span class="text-xs text-neutral-500">{{ student.email }}</span>
                  </div>
                </div>
              </div>
            </label>
            <div
              v-if="addStudentsFiltered.length === 0"
              class="px-4 py-3 text-sm text-neutral-500"
            >
              No hay estudiantes disponibles.
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
            @click="closeAddStudentsModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            :disabled="addStudentsSelectedIds.length === 0"
            @click="confirmAddStudentsToGroup"
          >
            Añadir
          </button>
        </div>
      </div>
    </div>

    <TeacherInviteModal
      :show="showInviteModal"
      :invite-group-name="inviteGroupName"
      :invite-emails="inviteEmails"
      :invite-message="inviteMessage"
      :invite-link="inviteLink"
      :protect-invite-code="protectInviteCode"
      :invite-code="inviteCode"
      :limit-invite-uses="limitInviteUses"
      @close="closeInviteModal"
      @send="sendInvites"
      @copy-invite-link="copyInviteLink"
      @update-field="updateInviteField"
    />

    <div
      v-if="showAddToGroupModal"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      @click.self="closeAddToGroupModal"
    >
      <div class="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
        <h2 class="text-lg font-semibold text-neutral-800 mb-2">Agregar a grupo</h2>
        <p class="text-sm text-neutral-500 mb-4">
          Selecciona uno o varios grupos para agregar
          {{ addToGroupStudentIds.length === 1 ? "al estudiante" : "a los estudiantes seleccionados" }}.
        </p>

        <div class="flex items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-600">
          <svg class="h-4 w-4 text-neutral-500" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
            <path d="M20 20L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          <input
            v-model.trim="addToGroupSearch"
            type="text"
            placeholder="Buscar grupo..."
            class="w-full bg-transparent text-sm text-neutral-700 focus:outline-none"
          />
        </div>

        <div class="mt-4 max-h-56 overflow-y-auto rounded-lg border border-neutral-200">
          <label
            v-for="group in addToGroupFilteredGroups"
            :key="group.id"
            class="flex items-center justify-between px-4 py-2 text-sm text-neutral-700 border-b border-neutral-100 last:border-b-0"
          >
            <div class="flex items-center gap-3">
              <input
                type="checkbox"
                class="h-4 w-4"
                :value="group.id"
                v-model="addToGroupGroupIds"
              />
              <span>{{ group.name }}</span>
            </div>
            <span class="text-xs text-neutral-500">{{ group.count }} estudiantes</span>
          </label>
          <div
            v-if="addToGroupFilteredGroups.length === 0"
            class="px-4 py-3 text-sm text-neutral-500"
          >
            No se encontraron grupos.
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
            @click="closeAddToGroupModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            :disabled="addToGroupGroupIds.length === 0"
            @click="confirmAddToGroup"
          >
            Agregar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TeacherCreateGroupModal from "./TeacherCreateGroupModal.vue";
import TeacherEditGroupModal from "./TeacherEditGroupModal.vue";
import TeacherInviteModal from "./TeacherInviteModal.vue";
import TeacherGroupsSidebar from "./TeacherGroupsSidebar.vue";
import IconifyIcon from "@iconify/vue";
import { MobAvatar } from "@/views/V2/components";
import bxRefresh from "@iconify/icons-bx/bx-refresh";
import {
  addStudentToGroup,
  createTeacherGroup,
  getTeacherGroups,
  getTeacherStudents,
  deleteTeacherGroup,
  cloneTeacherGroup,
  removeStudentFromGroup,
  deleteTeacherStudentRelation,
  updateTeacherGroup,
} from "@/services/teacher-groups.service";
import {
  deleteTeacherInvitation,
  sendTeacherInvitations,
} from "@/services/teacher-invitations.service";

export default {
  name: "TeacherGroups",
  components: {
    TeacherCreateGroupModal,
    TeacherEditGroupModal,
    TeacherInviteModal,
    TeacherGroupsSidebar,
    IconifyIcon,
    MobAvatar,
  },
  data() {
    return {
      search: "",
      orderBy: "name",
      activeGroupId: "all",
      activeStatus: "accepted",
      currentPage: 1,
      pageSize: 15,
      statusTabs: [
        { label: "Aceptados", value: "accepted" },
        { label: "Pendientes", value: "pending" },
      ],
      bulkAction: "",
      selectedStudents: [],
      isCreating: false,
      showCreateGroupModal: false,
      showInviteModal: false,
      showEditGroupModal: false,
      showAddToGroupModal: false,
      showAddStudentsModal: false,
      addStudentsSearch: "",
      addStudentsSelectedIds: [],
      addToGroupGroupIds: [],
      addToGroupSearch: "",
      addToGroupStudentIds: [],
      openRowMenuId: null,
      currentRowMenuStudent: null,
      openGroupMenuId: null,
      editGroupForm: {
        id: null,
        name: "",
        description: "",
      },
      editGroupStudents: [],
      inviteGroupName: "",
      inviteCourseName: "",
      inviteEmails: "",
      inviteMessage: "",
      inviteLink: "https://mobiusml.com/invite/ABC123XY",
      protectInviteCode: false,
      inviteCode: "MOB-GRP-1234",
      limitInviteUses: false,
      showValidation: false,
      form: {
        name: "",
        description: "",
      },
      icons: {
        bxRefresh,
      },
      groupList: [],
      students: [],
      groups: [
        {
          id: 1,
          name: "Grupo Alfa",
          description: "Nivel básico - 2026",
          courses: [],
        },
        {
          id: 2,
          name: "Grupo Beta",
          description: "Nivel intermedio - 2026",
          courses: [],
        },
      ],
    };
  },
  computed: {
    canCreate() {
      return Boolean(this.form.name);
    },
    isAllSelected() {
      return this.paginatedStudents.length > 0 &&
        this.paginatedStudents.every((student) => this.selectedStudents.includes(student.id));
    },
    totalPages() {
      return Math.ceil(this.filteredStudents.length / this.pageSize);
    },
    paginationStart() {
      if (this.filteredStudents.length === 0) {
        return 0;
      }
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.pageSize, this.filteredStudents.length);
    },
    paginatedStudents() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredStudents.slice(start, start + this.pageSize);
    },
    bulkActionOptions() {
      const selected = this.students.filter((student) =>
        this.selectedStudents.includes(student.id)
      );
      const hasPending = selected.some((student) => student.status === "pending");
      const hasAccepted = selected.some((student) => student.status === "accepted");

      if (hasPending && !hasAccepted) {
        return ["Recordar invitación", "Eliminar invitación"];
      }

      return [
        "Agregar a grupo",
        "Quitar de grupo",
        // "Enviar mensaje (próximamente)",
        "Eliminar relación profesor-alumno",
      ];
    },
    addToGroupGroupOptions() {
      const options = this.groupList.filter(
        (g) => g.id != null && Number.isFinite(Number(g.id))
      );
      if (!this.addToGroupStudentIds.length) {
        return options;
      }
      const selectedStudents = this.students.filter((student) =>
        this.addToGroupStudentIds.includes(student.id)
      );
      return options.filter((group) =>
        selectedStudents.some(
          (student) => !student.groups.includes(group.name)
        )
      );
    },
    addToGroupFilteredGroups() {
      const options = this.addToGroupGroupOptions;
      if (!this.addToGroupSearch) {
        return options;
      }
      const term = this.addToGroupSearch.toLowerCase();
      return options.filter((group) => group.name.toLowerCase().includes(term));
    },
    addStudentsCandidates() {
      const currentGroupName = this.editGroupForm.name;
      return this.students.filter(
        (student) =>
          student.status === "accepted" &&
          !student.groups.includes(currentGroupName)
      );
    },
    addStudentsFiltered() {
      const base = this.addStudentsCandidates;
      if (!this.addStudentsSearch) {
        return base;
      }
      const term = this.addStudentsSearch.toLowerCase();
      return base.filter(
        (student) =>
          student.name.toLowerCase().includes(term) ||
          student.email.toLowerCase().includes(term)
      );
    },
    addStudentsAllSelected() {
      return this.addStudentsFiltered.length > 0 &&
        this.addStudentsFiltered.every((student) =>
          this.addStudentsSelectedIds.includes(student.id)
        );
    },
    filteredStudents() {
      let results = [...this.students];

      if (this.activeGroupId !== "all") {
        const activeGroup = this.groupList.find(
          (group) => group.id === this.activeGroupId
        );
        const activeGroupName = activeGroup ? activeGroup.name : String(this.activeGroupId);
        results = results.filter((student) => student.groups.includes(activeGroupName));
      }

      if (this.activeStatus !== "all") {
        results = results.filter((student) => student.status === this.activeStatus);
      }

      if (this.search) {
        const term = this.search.toLowerCase();
        results = results.filter(
          (student) =>
            student.name.toLowerCase().includes(term) ||
            student.email.toLowerCase().includes(term)
        );
      }

      if (this.orderBy === "email") {
        results.sort((a, b) => a.email.localeCompare(b.email));
      } else {
        results.sort((a, b) => a.name.localeCompare(b.name));
      }

      return results;
    },
  },
  watch: {
    activeStatus() {
      this.currentPage = 1;
    },
    activeGroupId() {
      this.currentPage = 1;
    },
    search() {
      this.currentPage = 1;
    },
    students() {
      this.currentPage = 1;
    },
    showAddStudentsModal(isOpen) {
      if (!isOpen) {
        this.addStudentsSearch = "";
        this.addStudentsSelectedIds = [];
      }
    },
  },
  methods: {
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    getSelectedInstitutionId() {
      const storedId = localStorage.getItem("teacher_current_institution_id");
      if (storedId) {
        return Number(storedId);
      }
      const storedInstitutions = JSON.parse(
        localStorage.getItem("teacher_institutions") || "[]"
      );
      return storedInstitutions.length ? storedInstitutions[0].id : null;
    },
    handleInstitutionChanged() {
      this.activeGroupId = "all";
      this.selectedStudents = [];
      this.openRowMenuId = null;
      this.closeGroupMenu();
      this.loadGroups();
      this.loadStudents();
    },
    async loadGroups() {
      const institutionId = this.getSelectedInstitutionId();
      if (!institutionId) {
        this.groupList = [];
        return;
      }
      try {
        const groups = await getTeacherGroups(institutionId);
        this.groupList = groups.map((group) => ({
          id: group.id,
          name: group.name,
          count: group.studentCount ?? 0,
          description: group.description,
        }));
      } catch (error) {
        console.error("Error cargando grupos", error);
        this.groupList = [];
      }
    },
    async loadStudents() {
      const institutionId = this.getSelectedInstitutionId();
      if (!institutionId) {
        this.students = [];
        return;
      }
      try {
        const students = await getTeacherStudents(institutionId);
        this.students = students.map((student) => ({
          id: student.id,
          name: student.name,
          email: student.email,
          status: student.status,
          acceptedAt: student.acceptedAt,
          groups: student.groups
            ? student.groups.split(",").map((name) => name.trim()).filter(Boolean)
            : [],
        }));
      } catch (error) {
        console.error("Error cargando estudiantes", error);
        this.students = [];
      }
    },
    normalizeInputValue(value) {
      return typeof value === "string" ? value.trim() : value;
    },
    updateCreateGroupField({ field, value }) {
      this.form = {
        ...this.form,
        [field]: this.normalizeInputValue(value),
      };
    },
    updateEditGroupField({ field, value }) {
      this.editGroupForm = {
        ...this.editGroupForm,
        [field]: this.normalizeInputValue(value),
      };
    },
    updateInviteField({ field, value }) {
      this[field] = this.normalizeInputValue(value);
    },
    buildCloneName(baseName) {
      const normalized = (name) => name.toLowerCase();
      let candidate = `${baseName} (copia)`;
      const existing = new Set(this.groupList.map((group) => normalized(group.name)));
      if (!existing.has(normalized(candidate))) {
        return candidate;
      }
      let counter = 2;
      while (existing.has(normalized(`${baseName} (copia ${counter})`))) {
        counter += 1;
      }
      return `${baseName} (copia ${counter})`;
    },
    getActiveGroupName() {
      if (this.activeGroupId === "all") {
        return null;
      }
      const group = this.groupList.find((item) => item.id === this.activeGroupId);
      return group ? group.name : null;
    },
    toggleGroupMenu(groupId) {
      this.openGroupMenuId = this.openGroupMenuId === groupId ? null : groupId;
    },
    closeGroupMenu() {
      this.openGroupMenuId = null;
    },
    async deleteGroup(group) {
      this.closeGroupMenu();
      const confirmed = window.confirm(
        `¿Seguro que deseas eliminar el grupo "${group.name}"?`
      );
      if (!confirmed) {
        return;
      }
      const groupId = Number(group.id);
      if (!Number.isFinite(groupId)) {
        this.groupList = this.groupList.filter((item) => item.id !== group.id);
        return;
      }
      try {
        await deleteTeacherGroup(groupId);
        this.groupList = this.groupList.filter((item) => item.id !== groupId);
        this.groups = this.groups.filter((item) => item.id !== groupId);
        if (this.activeGroupId === groupId) {
          this.activeGroupId = "all";
        }
        await this.loadStudents();
      } catch (error) {
        console.error("Error eliminando grupo", error);
      }
    },
    async cloneGroup(group) {
      this.closeGroupMenu();
      const groupId = Number(group.id);
      if (!Number.isFinite(groupId)) {
        const newId = Date.now();
        const newName = this.buildCloneName(group.name);
        this.groupList.unshift({
          id: newId,
          name: newName,
          count: group.count || 0,
          description: group.description || null,
        });
        this.groups.unshift({
          id: newId,
          name: newName,
          description: group.description || "Sin descripción",
          courses: [],
        });
        this.activeGroupId = newId;
        return;
      }
      try {
        const created = await cloneTeacherGroup(groupId);
        await this.loadGroups();
        await this.loadStudents();
        this.groups.unshift({
          id: created.id,
          name: created.name,
          description: created.description || "Sin descripción",
          courses: [],
        });
        this.activeGroupId = created.id;
      } catch (error) {
        console.error("Error clonando grupo", error);
      }
    },
    async removeStudentFromActiveGroup(student) {
      const activeGroupName = this.getActiveGroupName();
      if (!activeGroupName) {
        window.alert("Selecciona un grupo para quitar al estudiante.");
        return;
      }
      const groupId = Number(this.activeGroupId);
      if (!Number.isFinite(groupId)) {
        this.students = this.students.map((item) =>
          item.id === student.id
            ? { ...item, groups: item.groups.filter((name) => name !== activeGroupName) }
            : item
        );
        return;
      }
      try {
        await removeStudentFromGroup(groupId, student.id);
        this.students = this.students.map((item) =>
          item.id === student.id
            ? { ...item, groups: item.groups.filter((name) => name !== activeGroupName) }
            : item
        );
        this.groupList = this.groupList.map((group) =>
          group.id === groupId
            ? { ...group, count: Math.max(0, (group.count || 0) - 1) }
            : group
        );
      } catch (error) {
        console.error("Error quitando estudiante del grupo", error);
      }
    },
    async removeInvitation(student) {
      const institutionId = this.getSelectedInstitutionId();
      if (!institutionId) {
        this.$toasted.error("Selecciona una institución antes de eliminar invitaciones.", {
          position: "top-center",
          duration: 3000,
        });
        return;
      }
      const confirmed = window.confirm(
        `¿Seguro que deseas eliminar la invitación de ${student.name}?`
      );
      if (!confirmed) {
        return;
      }
      try {
        await deleteTeacherInvitation(student.id, institutionId);
        this.students = this.students.filter((item) => item.id !== student.id);
        this.selectedStudents = this.selectedStudents.filter((id) => id !== student.id);
        this.$toasted.success("Invitación eliminada.", {
          position: "top-center",
          duration: 3000,
        });
      } catch (error) {
        console.error("Error eliminando invitación", error);
        this.$toasted.error("No se pudo eliminar la invitación.", {
          position: "top-center",
          duration: 3000,
        });
      }
    },
    handleRowAction(student, option) {
      if (option === "Quitar de grupo") {
        this.removeStudentFromActiveGroup(student);
      }
      if (option === "Agregar a grupo") {
        this.addToGroupStudentIds = [student.id];
        this.addToGroupGroupIds = [];
        this.addToGroupSearch = "";
        this.showAddToGroupModal = true;
        this.updateBodyModalClass();
      }
      if (option === "Eliminar relación profesor-alumno") {
        this.removeTeacherStudentRelation(student);
      }
      if (option === "Eliminar invitación") {
        this.removeInvitation(student);
      }
      this.closeRowMenu();
    },
    async handleBulkAction() {
      if (this.bulkAction === "Quitar de grupo") {
        await this.removeSelectedFromActiveGroup();
      }
      if (this.bulkAction === "Agregar a grupo") {
        this.addToGroupStudentIds = [...this.selectedStudents];
        this.addToGroupGroupIds = [];
        this.addToGroupSearch = "";
        this.showAddToGroupModal = true;
        this.updateBodyModalClass();
      }
      if (this.bulkAction === "Eliminar relación profesor-alumno") {
        await this.removeSelectedTeacherRelations();
      }
      this.bulkAction = "";
    },
    async removeTeacherStudentRelation(student) {
      if (!student || !student.id) {
        return;
      }
      const institutionId = this.getSelectedInstitutionId();
      if (!institutionId) {
        this.$toasted.error("Selecciona una institución antes de continuar.", {
          position: "top-center",
          duration: 3000,
        });
        return;
      }
      const confirmed = window.confirm(
        `¿Seguro que deseas eliminar la relación con ${student.name}? Se eliminará de todos tus grupos.`
      );
      if (!confirmed) {
        return;
      }
      try {
        await deleteTeacherStudentRelation(student.id, institutionId);
        this.$toasted.success("Relación eliminada.", {
          position: "top-center",
          duration: 3000,
        });
        await this.loadStudents();
        await this.loadGroups();
        this.selectedStudents = this.selectedStudents.filter((id) => id !== student.id);
      } catch (error) {
        console.error("Error eliminando relación profesor-alumno", error);
        this.$toasted.error("No se pudo eliminar la relación.", {
          position: "top-center",
          duration: 3000,
        });
      }
    },
    async removeSelectedTeacherRelations() {
      const institutionId = this.getSelectedInstitutionId();
      if (!institutionId) {
        this.$toasted.error("Selecciona una institución antes de continuar.", {
          position: "top-center",
          duration: 3000,
        });
        return;
      }
      const studentIds = [...this.selectedStudents];
      if (!studentIds.length) {
        return;
      }
      const confirmed = window.confirm(
        "¿Seguro que deseas eliminar la relación con los estudiantes seleccionados? Se eliminarán de todos tus grupos."
      );
      if (!confirmed) {
        return;
      }
      try {
        await Promise.all(
          studentIds.map((studentId) => deleteTeacherStudentRelation(studentId, institutionId))
        );
        this.$toasted.success("Relaciones eliminadas.", {
          position: "top-center",
          duration: 3000,
        });
        this.selectedStudents = [];
        await this.loadStudents();
        await this.loadGroups();
      } catch (error) {
        console.error("Error eliminando relaciones profesor-alumno", error);
        this.$toasted.error("No se pudieron eliminar las relaciones.", {
          position: "top-center",
          duration: 3000,
        });
      }
    },
    async removeSelectedFromActiveGroup() {
      const activeGroupName = this.getActiveGroupName();
      if (!activeGroupName) {
        this.$toasted.info("Primero debes seleccionar un grupo.", {
          position: "top-center",
          duration: 3000,
        });
        return;
      }
      const groupId = Number(this.activeGroupId);
      const studentIds = [...this.selectedStudents];
      if (!Number.isFinite(groupId)) {
        this.students = this.students.map((student) =>
          studentIds.includes(student.id)
            ? { ...student, groups: student.groups.filter((name) => name !== activeGroupName) }
            : student
        );
        this.selectedStudents = [];
        return;
      }
      try {
        await Promise.all(
          studentIds.map((studentId) => removeStudentFromGroup(groupId, studentId))
        );
        this.students = this.students.map((student) =>
          studentIds.includes(student.id)
            ? { ...student, groups: student.groups.filter((name) => name !== activeGroupName) }
            : student
        );
        const removedCount = studentIds.length;
        this.groupList = this.groupList.map((group) =>
          group.id === groupId
            ? { ...group, count: Math.max(0, (group.count || 0) - removedCount) }
            : group
        );
        this.selectedStudents = [];
      } catch (error) {
        console.error("Error quitando estudiantes del grupo", error);
      }
    },
    openEditGroupModal(group) {
      const numericId = Number(group.id);
      const resolvedId = Number.isFinite(numericId) ? numericId : null;
      this.editGroupForm = {
        id: resolvedId,
        name: group.name,
        description: group.description || "",
      };
      const groupStudents = this.students.filter((student) =>
        student.groups.includes(group.name)
      );
      this.editGroupStudents = groupStudents;
      this.showEditGroupModal = true;
      this.closeGroupMenu();
      this.updateBodyModalClass();
    },
    openEditGroupModalByName(groupName) {
      const selectedGroup = this.groupList.find((group) => group.name === groupName);
      this.openEditGroupModal(
        selectedGroup || { name: groupName, description: "" }
      );
    },
    closeEditGroupModal() {
      this.showEditGroupModal = false;
      this.updateBodyModalClass();
    },
    async removeStudentFromEditGroup(studentId) {
      const groupId = Number(this.editGroupForm.id);
      const groupName = this.editGroupForm.name;

      // Si no hay id numérico de grupo (caso raro), solo actualizamos el front
      if (!Number.isFinite(groupId)) {
        this.editGroupStudents = this.editGroupStudents.filter(
          (student) => student.id !== studentId
        );
        return;
      }

      try {
        await removeStudentFromGroup(groupId, studentId);

        // Actualizar lista dentro del modal
        this.editGroupStudents = this.editGroupStudents.filter(
          (student) => student.id !== studentId
        );

        // Sincronizar lista general de estudiantes
        this.students = this.students.map((student) =>
          student.id === studentId
            ? {
                ...student,
                groups: student.groups.filter((name) => name !== groupName),
              }
            : student
        );

        // Ajustar contador del grupo
        this.groupList = this.groupList.map((group) =>
          group.id === groupId
            ? { ...group, count: Math.max(0, (group.count || 0) - 1) }
            : group
        );
      } catch (error) {
        console.error("Error quitando estudiante del grupo (edición)", error);
      }
    },
    async removeSelectedFromEditGroup(studentIds) {
      const groupId = Number(this.editGroupForm.id);
      const groupName = this.editGroupForm.name;

      if (!studentIds || !studentIds.length) {
        return;
      }

      // Si no hay id numérico de grupo, solo tocamos el front
      if (!Number.isFinite(groupId)) {
        this.editGroupStudents = this.editGroupStudents.filter(
          (student) => !studentIds.includes(student.id)
        );
        return;
      }

      try {
        await Promise.all(
          studentIds.map((studentId) =>
            removeStudentFromGroup(groupId, studentId)
          )
        );

        // Actualizar lista del modal
        this.editGroupStudents = this.editGroupStudents.filter(
          (student) => !studentIds.includes(student.id)
        );

        // Sincronizar lista general de estudiantes
        this.students = this.students.map((student) =>
          studentIds.includes(student.id)
            ? {
                ...student,
                groups: student.groups.filter((name) => name !== groupName),
              }
            : student
        );

        // Ajustar contador del grupo
        const removedCount = studentIds.length;
        this.groupList = this.groupList.map((group) =>
          group.id === groupId
            ? {
                ...group,
                count: Math.max(0, (group.count || 0) - removedCount),
              }
            : group
        );
      } catch (error) {
        console.error("Error quitando estudiantes del grupo (edición)", error);
      }
    },
    toggleRowMenu(student) {
      if (!student || !student.id) {
        return;
      }
      if (this.openRowMenuId === student.id) {
        this.closeRowMenu();
        return;
      }
      this.openRowMenuId = student.id;
      this.currentRowMenuStudent = student;
    },
    closeRowMenu() {
      this.openRowMenuId = null;
      this.currentRowMenuStudent = null;
    },
    goToStudentReport(student) {
      this.closeRowMenu();
      if (student && student.id) {
        this.$router.push({ name: "TeacherStudentReport", params: { studentId: String(student.id) } });
      }
    },
    closeAllMenus() {
      this.closeRowMenu();
      this.closeGroupMenu();
    },
    closeAddToGroupModal() {
      this.showAddToGroupModal = false;
      this.addToGroupGroupIds = [];
      this.addToGroupSearch = "";
      this.addToGroupStudentIds = [];
      this.updateBodyModalClass();
    },
    async confirmAddToGroup() {
      const groupIds = this.addToGroupGroupIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      const count = this.addToGroupStudentIds.length;
      if (groupIds.length === 0 || !count) {
        return;
      }
      try {
        const requests = [];
        groupIds.forEach((groupId) => {
          this.addToGroupStudentIds.forEach((studentId) => {
            requests.push(addStudentToGroup(groupId, studentId));
          });
        });
        await Promise.all(requests);
        await this.loadStudents();
        await this.loadGroups();
        this.selectedStudents = [];
        this.closeAddToGroupModal();
        this.$toasted.success(
          count === 1
            ? "Estudiante agregado al grupo."
            : "Estudiantes agregados al grupo.",
          { position: "top-center", duration: 3000 }
        );
      } catch (err) {
        console.error("Error agregando al grupo", err);
        const msg =
          err.response?.data?.message ||
          err.response?.data ||
          "No se pudo agregar al grupo.";
        this.$toasted.error(typeof msg === "string" ? msg : "No se pudo agregar al grupo.", {
          position: "top-center",
          duration: 4000,
        });
      }
    },
    updateBodyModalClass() {
      const isOpen =
        this.showCreateGroupModal ||
        this.showInviteModal ||
        this.showEditGroupModal ||
        this.showAddToGroupModal ||
        this.showAddStudentsModal;
      const header = document.querySelector("header");
      if (isOpen) {
        document.body.classList.add("teacher-modal-open");
        if (header) {
          header.style.zIndex = "0";
        }
      } else {
        document.body.classList.remove("teacher-modal-open");
        if (header) {
          header.style.zIndex = "";
        }
      }
    },
    rowActionOptions(student) {
      if (student.status === "pending") {
        return ["Recordar invitación", "Eliminar invitación"];
      }

      return [
        "Agregar a grupo",
        "Quitar de grupo",
        // "Enviar mensaje (próximamente)",
        "Eliminar relación profesor-alumno",
      ];
    },
    setActiveGroup(groupId) {
      this.activeGroupId = groupId;
      const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
      if (isMobile && this.$refs.studentsTableSection) {
        this.$nextTick(() => {
          this.$refs.studentsTableSection.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedStudents = this.paginatedStudents.map((student) => student.id);
      } else {
        this.selectedStudents = [];
      }
    },
    openCreateGroupModal() {
      this.showValidation = false;
      this.showCreateGroupModal = true;
      this.updateBodyModalClass();
    },
    closeCreateGroupModal() {
      this.resetForm();
      this.showCreateGroupModal = false;
      this.updateBodyModalClass();
    },
    openInviteModal(group) {
      this.inviteGroupName = group ? group.name : "tus estudiantes";
      this.inviteCourseName = "";
      this.inviteEmails = "";
      this.inviteMessage = "";
      this.showInviteModal = true;
      this.updateBodyModalClass();
    },
    openInviteModalFromHeader() {
      const group = this.groupList.find((item) => item.id === this.activeGroupId);
      this.openInviteModal(group || { name: "tus estudiantes" });
    },
    openInviteModalFromEditGroup() {
      this.openInviteModal({ name: this.editGroupForm.name || "tus estudiantes" });
    },
    openAddStudentsModal() {
      this.showAddStudentsModal = true;
      this.addStudentsSearch = "";
      this.addStudentsSelectedIds = [];
      this.updateBodyModalClass();
    },
    closeAddStudentsModal() {
      this.showAddStudentsModal = false;
      this.addStudentsSearch = "";
      this.addStudentsSelectedIds = [];
      this.updateBodyModalClass();
    },
    toggleAddStudentsSelectAll(event) {
      if (event.target.checked) {
        this.addStudentsSelectedIds = this.addStudentsFiltered.map((student) => student.id);
      } else {
        this.addStudentsSelectedIds = [];
      }
    },
    closeInviteModal() {
      this.showInviteModal = false;
      this.updateBodyModalClass();
    },
    resetSelection() {
      this.selectedStudents = [];
      this.bulkAction = "";
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages) {
        return;
      }
      this.currentPage = page;
    },
    copyInviteLink() {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(this.inviteLink);
      }
    },
    async sendInvites() {
      const emails = this.parseEmails(this.inviteEmails);
      if (emails.length === 0) {
        this.$toasted.info("Ingresa al menos un correo válido.", {
          position: "top-center",
          duration: 3000,
        });
        return;
      }
      const institutionId = this.getSelectedInstitutionId();
      if (!institutionId) {
        this.$toasted.error("Selecciona una institución antes de enviar invitaciones.", {
          position: "top-center",
          duration: 3000,
        });
        return;
      }
      try {
        const result = await sendTeacherInvitations(emails, this.inviteMessage, institutionId);
        const created = result.created ? result.created.length : 0;
        const existing = result.existing ? result.existing.length : 0;
        const notFound = result.notFound ? result.notFound.length : 0;
        this.$toasted.success(
          `Solicitudes enviadas: ${created}. Existentes: ${existing}. No encontrados: ${notFound}.`,
          { position: "top-center", duration: 4000 }
        );
        this.showInviteModal = false;
        this.updateBodyModalClass();
        this.inviteEmails = "";
        this.inviteMessage = "";
        await this.loadStudents();
      } catch (error) {
        console.error("Error enviando invitaciones", error);
        this.$toasted.error("No se pudieron enviar las invitaciones.", {
          position: "top-center",
          duration: 4000,
        });
      }
    },
    async confirmAddStudentsToGroup() {
      const groupId = Number(this.editGroupForm.id);
      const studentIds = [...this.addStudentsSelectedIds];
      if (!Number.isFinite(groupId) || studentIds.length === 0) {
        return;
      }
      try {
        await Promise.all(
          studentIds.map((studentId) => addStudentToGroup(groupId, studentId))
        );
        await this.loadStudents();
        await this.loadGroups();
        const groupName = this.editGroupForm.name;
        this.editGroupStudents = this.students.filter((student) =>
          student.groups.includes(groupName)
        );
        this.closeAddStudentsModal();
        this.$toasted.success(
          studentIds.length === 1
            ? "Estudiante agregado al grupo."
            : "Estudiantes agregados al grupo.",
          { position: "top-center", duration: 3000 }
        );
      } catch (error) {
        console.error("Error agregando estudiantes al grupo", error);
        this.$toasted.error("No se pudieron agregar estudiantes.", {
          position: "top-center",
          duration: 3000,
        });
      }
    },
    async createGroup() {
      this.showValidation = true;
      if (!this.canCreate) {
        return;
      }
      const institutionId = this.getSelectedInstitutionId();
      if (!institutionId) {
        this.isCreating = false;
        return;
      }
      this.isCreating = true;
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description || null,
          institutionId,
        };
        const created = await createTeacherGroup(payload);
        this.groups.unshift({
          id: created.id,
          name: created.name,
          description: created.description || "Sin descripción",
          courses: [],
        });
        this.groupList.unshift({
          id: created.id,
          name: created.name,
          count: 0,
          description: created.description || null,
        });
        this.closeCreateGroupModal();
      } catch (error) {
        // error controlado por backend; no rompemos el flujo de UI
        console.error("Error creando grupo", error);
      } finally {
        this.isCreating = false;
      }
    },
    parseEmails(value) {
      if (!value) {
        return [];
      }
      return value
        .split(/[,;\n]/g)
        .map((email) => email.trim())
        .filter((email) => email.length > 0);
    },
    async saveEditGroup() {
      if (!this.editGroupForm.id) {
        this.closeEditGroupModal();
        return;
      }
      try {
        const payload = {
          name: this.editGroupForm.name,
          description: this.editGroupForm.description || null,
        };
        const updated = await updateTeacherGroup(this.editGroupForm.id, payload);
        this.groupList = this.groupList.map((group) =>
          group.id === updated.id
            ? { ...group, name: updated.name, description: updated.description }
            : group
        );
        this.groups = this.groups.map((group) =>
          group.id === updated.id
            ? { ...group, name: updated.name, description: updated.description }
            : group
        );
      } catch (error) {
        console.error("Error actualizando grupo", error);
      } finally {
        this.closeEditGroupModal();
      }
    },
    resetForm() {
      this.form = {
        name: "",
        description: "",
      };
      this.showValidation = false;
    },
  },
  beforeDestroy() {
    document.body.classList.remove("teacher-modal-open");
    document.removeEventListener("click", this.closeAllMenus);
    this.$root.$off("teacher-institution-change", this.handleInstitutionChanged);
  },
  mounted() {
    document.addEventListener("click", this.closeAllMenus);
    this.$root.$on("teacher-institution-change", this.handleInstitutionChanged);
    this.loadGroups();
    this.loadStudents();
  },
};
</script>
